import { useState } from 'react';
import { useTranslation } from '../../../i18n/useTranslation';
import type { LicenseFilterState } from '../../../types/manageLicenses';

interface Props {
  filters: LicenseFilterState;
  onChange: (patch: Partial<LicenseFilterState>) => void;
}

export default function LicenseAdvancedFilters({ filters, onChange }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const hasAdvanced =
    filters.publisherIds.length > 0 ||
    filters.bookIds.length > 0 ||
    filters.booksellerIds.length > 0 ||
    !!filters.activatedAfter ||
    !!filters.activatedBefore ||
    !!filters.expiresAfter ||
    !!filters.expiresBefore ||
    filters.isPerpetual !== undefined ||
    filters.hasExternalLicenseId !== undefined ||
    filters.hasActiveSessions !== undefined ||
    filters.atOrOverDeviceLimit ||
    filters.includeDeleted;

  return (
    <div className="mb-3 border border-neutral-200 rounded">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full text-left px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 flex items-center justify-between"
        aria-expanded={open}
      >
        <span>{t('App.Admin.Licenses.LicenseAdvancedFilters.Title')} {hasAdvanced && <span className="text-primary-600">•</span>}</span>
        <span className="text-xs text-neutral-500">{open ? t('App.Admin.Licenses.LicenseAdvancedFilters.Hide') : t('App.Admin.Licenses.LicenseAdvancedFilters.Show')}</span>
      </button>

      {open && (
        <div className="p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 border-t border-neutral-200">

          {/* Publisher ID */}
          <label className="flex flex-col text-xs text-neutral-600">
            {t('App.Admin.Licenses.LicenseAdvancedFilters.PublisherIdLabel')}
            <input
              type="text"
              placeholder={t('App.Admin.Licenses.LicenseAdvancedFilters.PublisherIdPlaceholder')}
              value={filters.publisherIds[0] ?? ''}
              onChange={(e) => onChange({
                publisherIds: e.target.value.trim() ? [e.target.value.trim()] : [],
                page: 1,
              })}
              className="border border-neutral-300 rounded px-2 py-1 text-sm mt-1"
            />
          </label>

          {/* Book ID */}
          <label className="flex flex-col text-xs text-neutral-600">
            {t('App.Admin.Licenses.LicenseAdvancedFilters.BookIdLabel')}
            <input
              type="text"
              placeholder={t('App.Admin.Licenses.LicenseAdvancedFilters.BookIdPlaceholder')}
              value={filters.bookIds[0] ?? ''}
              onChange={(e) => onChange({
                bookIds: e.target.value.trim() ? [e.target.value.trim()] : [],
                page: 1,
              })}
              className="border border-neutral-300 rounded px-2 py-1 text-sm mt-1"
            />
          </label>

          {/* Bookseller ID */}
          <label className="flex flex-col text-xs text-neutral-600">
            {t('App.Admin.Licenses.LicenseAdvancedFilters.BooksellerIdLabel')}
            <input
              type="text"
              placeholder={t('App.Admin.Licenses.LicenseAdvancedFilters.BooksellerIdPlaceholder')}
              value={filters.booksellerIds[0] ?? ''}
              onChange={(e) => onChange({
                booksellerIds: e.target.value.trim() ? [e.target.value.trim()] : [],
                page: 1,
              })}
              className="border border-neutral-300 rounded px-2 py-1 text-sm mt-1"
            />
          </label>

          {/* Activated after */}
          <label className="flex flex-col text-xs text-neutral-600">
            {t('App.Admin.Licenses.LicenseAdvancedFilters.ActivatedAfter')}
            <input
              type="date"
              value={filters.activatedAfter?.slice(0, 10) ?? ''}
              onChange={(e) => onChange({
                activatedAfter: e.target.value ? `${e.target.value}T00:00:00Z` : undefined,
                page: 1,
              })}
              className="border border-neutral-300 rounded px-2 py-1 text-sm mt-1"
            />
          </label>

          {/* Activated before */}
          <label className="flex flex-col text-xs text-neutral-600">
            {t('App.Admin.Licenses.LicenseAdvancedFilters.ActivatedBefore')}
            <input
              type="date"
              value={filters.activatedBefore?.slice(0, 10) ?? ''}
              onChange={(e) => onChange({
                activatedBefore: e.target.value ? `${e.target.value}T23:59:59Z` : undefined,
                page: 1,
              })}
              className="border border-neutral-300 rounded px-2 py-1 text-sm mt-1"
            />
          </label>

          {/* Expires after */}
          <label className="flex flex-col text-xs text-neutral-600">
            {t('App.Admin.Licenses.LicenseAdvancedFilters.ExpiresAfter')}
            <input
              type="date"
              value={filters.expiresAfter?.slice(0, 10) ?? ''}
              onChange={(e) => onChange({
                expiresAfter: e.target.value ? `${e.target.value}T00:00:00Z` : undefined,
                page: 1,
              })}
              className="border border-neutral-300 rounded px-2 py-1 text-sm mt-1"
            />
          </label>

          {/* Expires before */}
          <label className="flex flex-col text-xs text-neutral-600">
            {t('App.Admin.Licenses.LicenseAdvancedFilters.ExpiresBefore')}
            <input
              type="date"
              value={filters.expiresBefore?.slice(0, 10) ?? ''}
              onChange={(e) => onChange({
                expiresBefore: e.target.value ? `${e.target.value}T23:59:59Z` : undefined,
                page: 1,
              })}
              className="border border-neutral-300 rounded px-2 py-1 text-sm mt-1"
            />
          </label>

          {/* Is Perpetual */}
          <label className="flex flex-col text-xs text-neutral-600">
            {t('App.Admin.Licenses.LicenseAdvancedFilters.IsPerpetualLabel')}
            <select
              value={filters.isPerpetual === undefined ? '' : String(filters.isPerpetual)}
              onChange={(e) => onChange({
                isPerpetual: e.target.value === '' ? undefined : e.target.value === 'true',
                page: 1,
              })}
              className="border border-neutral-300 rounded px-2 py-1 text-sm mt-1"
            >
              <option value="">{t('App.Common.FilterAny')}</option>
              <option value="true">{t('App.Admin.Licenses.LicenseAdvancedFilters.IsPerpetualPerpetual')}</option>
              <option value="false">{t('App.Admin.Licenses.LicenseAdvancedFilters.IsPerpetualTerm')}</option>
            </select>
          </label>

          {/* Has ExternalLicenseId */}
          <label className="flex flex-col text-xs text-neutral-600">
            {t('App.Admin.Licenses.LicenseAdvancedFilters.HasExternalId')}
            <select
              value={filters.hasExternalLicenseId === undefined ? '' : String(filters.hasExternalLicenseId)}
              onChange={(e) => onChange({
                hasExternalLicenseId: e.target.value === '' ? undefined : e.target.value === 'true',
                page: 1,
              })}
              className="border border-neutral-300 rounded px-2 py-1 text-sm mt-1"
            >
              <option value="">{t('App.Common.FilterAny')}</option>
              <option value="true">{t('App.Common.Yes')}</option>
              <option value="false">{t('App.Common.No')}</option>
            </select>
          </label>

          {/* Has Active Sessions */}
          <label className="flex flex-col text-xs text-neutral-600">
            {t('App.Admin.Licenses.LicenseAdvancedFilters.HasActiveSessions')}
            <select
              value={filters.hasActiveSessions === undefined ? '' : String(filters.hasActiveSessions)}
              onChange={(e) => onChange({
                hasActiveSessions: e.target.value === '' ? undefined : e.target.value === 'true',
                page: 1,
              })}
              className="border border-neutral-300 rounded px-2 py-1 text-sm mt-1"
            >
              <option value="">{t('App.Common.FilterAny')}</option>
              <option value="true">{t('App.Common.Yes')}</option>
              <option value="false">{t('App.Common.No')}</option>
            </select>
          </label>

          {/* At or over device limit */}
          <label className="flex items-center gap-2 text-sm text-neutral-700 mt-5">
            <input
              type="checkbox"
              checked={filters.atOrOverDeviceLimit}
              onChange={(e) => onChange({ atOrOverDeviceLimit: e.target.checked, page: 1 })}
            />
            {t('App.Admin.Licenses.LicenseAdvancedFilters.AtOrOverDeviceLimit')}
          </label>

          {/* Include deleted */}
          <label className="flex items-center gap-2 text-sm text-neutral-700 mt-5">
            <input
              type="checkbox"
              checked={filters.includeDeleted}
              onChange={(e) => onChange({ includeDeleted: e.target.checked, page: 1 })}
            />
            {t('App.Admin.Licenses.LicenseAdvancedFilters.IncludeDeleted')}
          </label>

        </div>
      )}
    </div>
  );
}
